import React from 'react';
import processEditorialContent from './processEditorialContent';
import { useMessages, useConfig } from '@ynap/immutables-provider';
import { SizeChartLink } from '../';
import ReactMarkdown from 'markdown-to-jsx';
import { SingleBadge } from '@ynap/badge';

const editorialIds = {
    SIZE_AND_FIT: 'SIZE_AND_FIT',
    EDITORS_NOTES: 'EDITORS_NOTES',
    TECHNICAL_DESCRIPTION: 'TECHNICAL_DESCRIPTION',
    DETAILS_AND_CARE: 'DETAILS_AND_CARE',
    DELIVERY_AND_RETURNS: 'DELIVERY_AND_RETURNS',
    CONSIDERED_INFO: 'CONSIDERED_INFO',
};

export const getFitAndMeasurements = (measurements, sizeDetails, productMeasurementsEnabled) => {
    if (!measurements?.length > 0) return null;
    return (
        <>
            {productMeasurementsEnabled && <p>{sizeDetails.productMeasurements?.()}</p>}
            <ul>
                {measurements?.map((measurement, index) => {
                    return measurementItem(measurement, index, sizeDetails);
                })}
            </ul>
        </>
    );
};

const measurementItem = (measurement, index, sizeDetails) => {
    // Handles multiple values and returns their measurement value if present. Will
    // effect bags or accessiories
    return (
        <li key={index}>
            {measurement.label && `${measurement.label}: `}
            {measurement.values.length > 1
                ? measurement.values.map((value, index) => {
                      const defaultDetails = index === 0 ? `${value.label}${value?.uom}` : ` / ${value.label}${value?.uom}`;
                      const formattedDetails = sizeDetails.sizeDetails?.({
                          slash: index === 0 ? undefined : 'slash',
                          label: value?.label,
                          uom: value?.uom,
                      });

                      return formattedDetails || defaultDetails;
                  })
                : `${measurement.values[0].label}`}
        </li>
    );
};

const getEditorials = ({
    setSizeChartModal,
    features,
    madeIn,
    productNotes,
    sizeConversion,
    sizeMeasurement,
    internal_urls,
    locale,
    skus,
    customerCareUrl,
    bem,
    shippingToCountry,
    deliveryInfo,
    dropshipInfo,
    deliveryLink,
    returnsLink,
    isOneSize,
    selectedPartNumber,
    fullfilledBy,
    sizeDetails,
    hasMeaningfulSize,
    considered,
    consideredLabels,
    sustainabilityDisclaimers,
    onLinkClick,
    finalSale,
    masterCategory,
    isEip,
    enableDeliveryAndReturnCustomMessage,
    country,
}) => {
    const messages = useMessages();
    const config = useConfig();
    const hideAccordionSections = config?.head?.pdp?.hideAccordionSections;
    const productMeasurementsEnabled = config?.head?.pdp?.productMeasurements;
    const urlConfig = config?.global?.urlConfig;
    let { editorialDescription, technicalDescription, detailsAndCare, sizeAndFit, fitAndMeasurements } = productNotes || {};
    const { SIZE_AND_FIT, EDITORS_NOTES, TECHNICAL_DESCRIPTION, DETAILS_AND_CARE, DELIVERY_AND_RETURNS, CONSIDERED_INFO } = editorialIds;
    const editorialConfig = {
        locale,
        urlConfig,
        internal_urls,
        selectedPartNumber,
    };
    const showSizeAndFit = !features?.hideSizeAndFit;

    /*** Editor's Notes ***/
    const editorsNotesSection = {
        content: processEditorialContent({
            ...editorialConfig,
            content: editorialDescription,
            editorialId: EDITORS_NOTES,
        }),
        heading: messages.accordionTitles.editorsNotes(),
        id: EDITORS_NOTES,
    };

    /*** Size and Fit ***/
    const measurements = getFitAndMeasurements(fitAndMeasurements, sizeDetails, productMeasurementsEnabled);
    const sizeAndFitInformation = (sizeAndFit || measurements) && (
        <>
            {measurements && isOneSize && !sizeAndFit && messages.sizeAndFit?.measurements && (
                <p className={bem('measurementSubTitle')}>
                    <b>{messages.sizeAndFit.measurements?.()}</b>
                </p>
            )}
            {processEditorialContent({
                ...editorialConfig,
                content: sizeAndFit,
                editorialId: SIZE_AND_FIT,
            })}
            {measurements}
        </>
    );

    const sizeAndFitSection = {
        content: showSizeAndFit && sizeAndFitInformation && (
            <>
                {sizeAndFitInformation}
                {(!measurements || hasMeaningfulSize) && (
                    <SizeChartLink
                        dataTestId="accordion-sizeguide-link"
                        setSizeChartModal={setSizeChartModal}
                        sizeMeasurement={sizeMeasurement}
                        sizeConversion={sizeConversion}
                        className={bem('accordionSizeguideLink')}
                        isOneSize={isOneSize}
                        hasMeaningfulSize={hasMeaningfulSize}
                    >
                        {messages.sizes.sizeGuide.viewProductMeasurements()}
                    </SizeChartLink>
                )}

                {customerCareUrl && (
                    <p className={bem('advice')}>
                        {messages.product.needAdvice()} <a href={customerCareUrl}> {messages.product.needAdviceCTA()} </a>
                    </p>
                )}
            </>
        ),
        heading: messages.accordionTitles.sizeAndFit(),
        id: SIZE_AND_FIT,
    };

    /*** Details and Care ***/
    if (madeIn && !technicalDescription) {
        madeIn === 'Imported'
            ? (detailsAndCare += `- ${messages.detailsAndCare.imported()}`)
            : (detailsAndCare += `- ${messages.detailsAndCare.madeIn({ madeIn })}`);
    }
    const detailsAndCareSection = {
        content: processEditorialContent({
            ...editorialConfig,
            content: detailsAndCare,
            editorialId: DETAILS_AND_CARE,
        }),
        heading: messages.accordionTitles.detailsAndCare(),
        id: DETAILS_AND_CARE,
    };

    /*** Technical Description ***/
    const technicalDescriptionSection = {
        content: technicalDescription && (
            <div>
                {processEditorialContent({
                    ...editorialConfig,
                    content: technicalDescription,
                    editorialId: TECHNICAL_DESCRIPTION,
                })}
                {skus?.[0]?.composition && <span className={bem('composition')}>{skus[0].composition}</span>}
            </div>
        ),
        heading: messages.accordionTitles.technicalDescription(),
        id: TECHNICAL_DESCRIPTION,
    };

    /*** Delivery And Returns ***/
    const getDeliveryDesc = () => {
        const categoriesOdd = [
            'NAP-1582', //Beauty
            'NAP-1-1273-1275', //Accessories > Homeware > Candles
            'NAP-1780-1787', //Lifestyle > Home Fragrance
            'MRP-3151', //grooming,
            'MRP-1780-1787', //Lifestyle > Home Fragrance
        ];

        const lastCategory = masterCategory?.length ? masterCategory[masterCategory?.length - 1] : { identifier: '-' };
        const validCategory = lastCategory.identifier ? lastCategory : masterCategory[masterCategory?.length - 2];
        const messageInCategory = `${messages.deliveryAndReturns?.deliveryDescriptionCategories?.({ deliveryLink })} ${
            !finalSale ? messages.deliveryAndReturns?.returnsDescriptionCategories?.({ returnsLink }) : ''
        }`;
        const messageOutCategory = `${messages.deliveryAndReturns?.deliveryDescription?.({ deliveryLink })} ${
            !finalSale
                ? messages.deliveryAndReturns?.returnsDescription?.({
                      returnsLink,
                      country: enableDeliveryAndReturnCustomMessage && country.toUpperCase(),
                      isEip,
                  })
                : ''
        }`;

        return validCategory?.identifier && categoriesOdd.some((a) => validCategory?.identifier?.includes?.(a)) ? messageInCategory : messageOutCategory;
    };
    const deliveryInfoSection = {
        content: (deliveryInfo?.length || fullfilledBy || (deliveryLink && returnsLink)) && messages?.deliveryAndReturns?.deliveryDescription && (
            <>
                {deliveryInfo?.length ? (
                    <>
                        {deliveryInfo.map((data, i) => (
                            <div key={`deliverySection${i}`}>
                                <span className={bem('deliveryTitle')}>
                                    {processEditorialContent({
                                        content: `${data.deliveryType}: ${data.currency + data.price}`,
                                        editorialId: DELIVERY_AND_RETURNS,
                                        selectedPartNumber,
                                    })}
                                </span>
                                <div className={bem('deliveryInfo')}>
                                    {processEditorialContent({
                                        content: data.details,
                                        editorialId: DELIVERY_AND_RETURNS,
                                        selectedPartNumber,
                                    })}
                                </div>
                            </div>
                        ))}
                        {shippingToCountry && messages.deliveryAndReturns?.shippingToCountry && (
                            <div className={bem('freeReturns')}>{messages.deliveryAndReturns?.shippingToCountry?.({ country: shippingToCountry })}</div>
                        )}
                    </>
                ) : (
                    ''
                )}
                {dropshipInfo?.length ? (
                    <ul className={bem('deliveryInfoDropShip')}>
                        {dropshipInfo.map(({ icon, text }) => (
                            <li className={bem('deliveryInfoPoint', icon)} key={icon}>
                                {text}
                            </li>
                        ))}
                    </ul>
                ) : (
                    deliveryLink &&
                    returnsLink && (
                        <div className={bem(dropshipInfo?.length ? 'deliveryLinksDropShip' : 'freeReturns')}>
                            <ReactMarkdown
                                options={{
                                    overrides: {
                                        a: Link,
                                    },
                                }}
                            >
                                {getDeliveryDesc()}
                            </ReactMarkdown>
                        </div>
                    )
                )}
            </>
        ),
        heading: messages.accordionTitles?.deliveryAndReturns?.(),
        id: DELIVERY_AND_RETURNS,
    };

    /*** Considerd Information - TON Infinity ***/

    const consideredInfoSection = {
        content: considered && (
            <div className={bem('accordionContent', 'editors_notes')}>
                <p className={bem('accordionContent', 'considered')}>
                    {messages.considered.consideredAccordion()}
                    <span
                        onClick={(e) => {
                            e.preventDefault();
                            onLinkClick(`/${locale}${messages.considered.href()}`);
                            window.open(`/${locale}${messages.considered.href()}`);
                        }}
                    >
                        {messages.considered.actionText()}
                    </span>
                </p>
                <p className={bem('consideredTitle')}>{messages.considered.consideredAttributesTitle()}</p>
                {consideredLabels.map((label, i) => {
                    return (
                        <div className={bem('badgeWrapper')} key={i}>
                            <SingleBadge
                                className={bem('consideredBadge')}
                                considered={true}
                                badge={{ label: label, key: 'CONSIDERED_BADGE' }}
                                translation={label}
                            />
                        </div>
                    );
                })}

                {sustainabilityDisclaimers && (
                    <ul className={bem('sustainabilityDisclaimers')}>
                        {sustainabilityDisclaimers.length === 1 ? (
                            <li className={bem('sustainabilityDisclaimers', 'disclaimer')}>{sustainabilityDisclaimers[0]}</li>
                        ) : (
                            sustainabilityDisclaimers.map((disclaimer, i) => <li key={i}>{disclaimer}</li>)
                        )}
                    </ul>
                )}
            </div>
        ),
        heading: messages?.considered?.badge() || '',
        id: CONSIDERED_INFO,
    };

    /*** Remove sections with no content or listed in hideAccordionSections config and return ***/
    return [editorsNotesSection, sizeAndFitSection, detailsAndCareSection, technicalDescriptionSection, deliveryInfoSection, consideredInfoSection].filter(
        (i) => i?.content && !(hideAccordionSections || []).includes(i?.id),
    );
};

const Link = (props) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
        {props.children}
    </a>
);

export { getEditorials, editorialIds };
